import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Users,
    ChevronDown,
    ChevronUp,
    Building2,
    AlertTriangle,
    RefreshCw,
    Search,
    Download,
    ShieldAlert,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Plus,
    Target,
    Edit,
    Trash2,
    BarChart3,
    Eye,
    X,
} from 'lucide-react';
import {
    apiClient,
    SiteRiskLeaderboardEntry,
    CreateSiteRequest,
    UpdateSiteRequest,
    Site,
    BreakdownCategoryDefinition,
} from '../services/api';
import { iconComponents, severityColorStrings as severityColors } from '../utils/ui-mappings';
import { useScoringConfig } from '../contexts/ScoringConfigContext';
import { useSort } from '../hooks/useSort';
import SiteTrendsTab from './SiteTrendsTab';
import AddSiteModal from './Modals/AddSiteModal';
import EditSiteModal from './Modals/EditSiteModal';
import DeleteSiteModal from './Modals/DeleteSiteModal';
import SiteViewModal from './Modals/SiteViewModal';
import MultiSelectFilter from './MultiSelectFilter';

const ITEMS_PER_PAGE = 10;
const CARD = 'bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]';

const SitesPage: React.FC = () => {
    const navigate = useNavigate();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();
    const [leaderboard, setLeaderboard] = useState<SiteRiskLeaderboardEntry[]>([]);
    const [breakdownCategories, setBreakdownCategories] = useState<BreakdownCategoryDefinition[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showAddSite, setShowAddSite] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [showEditSite, setShowEditSite] = useState(false);
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const [showViewSite, setShowViewSite] = useState(false);
    const [selectedViewSite, setSelectedViewSite] = useState<SiteRiskLeaderboardEntry | null>(null);

    const [siteToDelete, setSiteToDelete] = useState<{ id: string; name: string; totalDrivers: number } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [riskFilters, setRiskFilters] = useState<string[]>([]);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [issueFilters, setIssueFilters] = useState<string[]>([]);
    const [siteActiveMap, setSiteActiveMap] = useState<Map<string, boolean>>(new Map());
    const { sortBy, sortOrder, toggleSort, setSortBy, setSortOrder } = useSort<'rank' | 'name' | 'score' | 'drivers'>('rank');

    const [currentPage, setCurrentPage] = useState(1);
    const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
    const [trendsRefreshKey, setTrendsRefreshKey] = useState(0);

    useEffect(() => {
        loadLeaderboard();
    }, []);

    const loadLeaderboard = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const [leaderboardRes, sitesRes] = await Promise.all([
                apiClient.getSiteRiskLeaderboard(),
                apiClient.getSites({}),
            ]);
            if (leaderboardRes.breakdownCategories) {
                setBreakdownCategories(leaderboardRes.breakdownCategories);
            }
            setSiteActiveMap(new Map(sitesRes.data.map(s => [s.id, s.active])));
            setLeaderboard(leaderboardRes.data);
        } catch (err: any) {
            setError(err.message || 'Failed to load sites');
            console.error('Error loading leaderboard:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSite = async (siteData: CreateSiteRequest) => {
        try {
            setIsCreating(true);
            await apiClient.createSite(siteData);
            toast.success('Site created successfully!');
            setShowAddSite(false);
            await loadLeaderboard();
        } catch (err: any) {
            toast.error(err.message || 'Failed to create site');
            console.error('Error creating site:', err);
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenEdit = async (siteId: string) => {
        try {
            const response = await apiClient.getSite(siteId);
            setSelectedSite(response.data);
            setShowEditSite(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load site details');
        }
    };

    const handleUpdateSite = async (siteId: string, siteData: UpdateSiteRequest) => {
        try {
            setIsUpdating(true);
            await apiClient.updateSite(siteId, siteData);
            toast.success('Site updated successfully!');
            setShowEditSite(false);
            setSelectedSite(null);
            await loadLeaderboard();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update site');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteSite = async (action: 'unassign' | 'reassign', targetSiteId?: string) => {
        if (!siteToDelete) return;
        try {
            setIsDeleting(true);
            await apiClient.deleteSite(siteToDelete.id, { action, targetSiteId });
            toast.success(`${siteToDelete.name} deleted successfully`);
            setSiteToDelete(null);
            await loadLeaderboard();
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete site');
        } finally {
            setIsDeleting(false);
        }
    };

    const filteredData = useMemo(() => {
        let result = [...leaderboard];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(site =>
                site.siteName.toLowerCase().includes(query) ||
                site.siteCode.toLowerCase().includes(query)
            );
        }

        if (riskFilters.length > 0) {
            result = result.filter(site => riskFilters.includes(site.riskLevel));
        }

        if (statusFilters.length > 0) {
            result = result.filter(site => {
                const isActive = siteActiveMap.get(site.siteId) ?? true;
                return statusFilters.includes(isActive ? 'active' : 'inactive');
            });
        }

        if (issueFilters.length > 0) {
            result = result.filter(site => issueFilters.some(f => (site.breakdown[f] || 0) > 0));
        }

        result.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'rank':    comparison = a.rank - b.rank; break;
                case 'name':    comparison = a.siteName.localeCompare(b.siteName); break;
                case 'score':   comparison = a.totalScore - b.totalScore; break;
                case 'drivers': comparison = a.totalDrivers - b.totalDrivers; break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [leaderboard, searchQuery, riskFilters, statusFilters, issueFilters, siteActiveMap, sortBy, sortOrder]);

    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredData, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, riskFilters, statusFilters, issueFilters, sortBy, sortOrder]);

    const sitesWithDrivers = leaderboard.filter(s => s.totalDrivers > 0);
    const riskDistribution = {
        low:       sitesWithDrivers.filter(s => s.riskLevel === 'low').length,
        medium:    sitesWithDrivers.filter(s => s.riskLevel === 'medium').length,
        high:      sitesWithDrivers.filter(s => s.riskLevel === 'high').length,
        noDrivers: leaderboard.filter(s => s.totalDrivers === 0).length,
    };

    const orgAverageScore = useMemo(() => {
        if (sitesWithDrivers.length === 0) return 0;
        return sitesWithDrivers.reduce((sum, s) => sum + s.totalScore, 0) / sitesWithDrivers.length;
    }, [sitesWithDrivers]);

    const getOrgScoreLevel = (score: number): 'low' | 'medium' | 'high' => {
        if (score < lowMediumBoundary) return 'low';
        if (score < mediumHighBoundary) return 'medium';
        return 'high';
    };

    const getRiskColor = (level: string): string => {
        switch (level) {
            case 'low':    return '#69BE28';
            case 'medium': return '#f59e0b';
            case 'high':   return '#ef4444';
            default:       return '#6b7280';
        }
    };

    const getRiskChipBg = (level: string): string => {
        switch (level) {
            case 'low':    return 'rgba(105,190,40,0.12)';
            case 'medium': return 'rgba(245,158,11,0.12)';
            case 'high':   return 'rgba(239,68,68,0.12)';
            default:       return 'rgba(107,114,128,0.1)';
        }
    };

    const getRankBadge = (rank: number) => {
        if (rank === 1) return <span className="text-lg">🥇</span>;
        if (rank === 2) return <span className="text-lg">🥈</span>;
        if (rank === 3) return <span className="text-lg">🥉</span>;
        return <span className="text-sm font-medium text-gray-600">{rank}</span>;
    };

    const exportToCSV = () => {
        const headers = ['Rank', 'Site Name', 'Site Code', 'Risk Score', 'Risk Level', 'Total Drivers', 'Trend', 'Previous Score'];
        const rows = filteredData.map(site => [
            site.rank,
            site.siteName,
            site.siteCode,
            site.totalScore.toFixed(2),
            site.riskLevel,
            site.totalDrivers,
            site.trend,
            site.previousScore?.toFixed(2) ?? 'N/A',
        ]);
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `site-risk-leaderboard-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
                <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8">
                    <div className={`${CARD} p-6`}>
                        <div className="flex items-center justify-center h-64">
                            <RefreshCw className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                            <span className="ml-3 text-gray-600 text-lg">Loading site risk data...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
                <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8">
                    <div className={`${CARD} p-6`}>
                        <div className="flex items-center justify-center h-64 text-red-600">
                            <AlertTriangle className="w-6 h-6 mr-2" />
                            <span className="text-lg">{error}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const scoreLevel = getOrgScoreLevel(orgAverageScore);

    return (
        <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
            <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8">

                {/* Action bar */}
                <div className="flex items-center justify-end gap-2 mb-5">
                    <button
                        type="button"
                        onClick={() => { loadLeaderboard(); setTrendsRefreshKey(k => k + 1); }}
                        disabled={isLoading}
                        className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-50"
                        aria-label="Refresh site data"
                        aria-busy={isLoading}
                    >
                        <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={exportToCSV}
                        className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2"
                    >
                        <Download className="mr-2 h-4 w-4" />
                        Export
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowAddSite(true)}
                        className="inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2"
                        style={{ backgroundColor: '#3d701a' }}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Site
                    </button>
                </div>

                {/* Stat tiles */}
                <div className="grid grid-cols-5 gap-4 mb-5">
                    {/* Company Score */}
                    <div className={`${CARD} p-4`}>
                        <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: getRiskChipBg(scoreLevel) }}>
                            <Target className="w-4 h-4" style={{ color: getRiskColor(scoreLevel) }} />
                        </div>
                        <p className="text-2xl font-bold leading-none" style={{ color: getRiskColor(scoreLevel) }}>
                            {orgAverageScore.toFixed(0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1.5">Company Score</p>
                    </div>

                    {/* Total Sites */}
                    <button
                        type="button"
                        className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${riskFilters.length === 0 ? 'border-gray-900' : 'border-gray-200/80'}`}
                        onClick={() => { setRiskFilters([]); setSortBy('rank'); setSortOrder('asc'); }}
                    >
                        <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${riskFilters.length === 0 ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                        <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(107,114,128,0.1)' }}>
                            <Building2 className="w-4 h-4 text-gray-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900 leading-none">{leaderboard.length}</p>
                        <p className="text-xs text-gray-500 mt-1.5">Total Sites</p>
                        <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                            All sites in your organisation.
                        </span>
                    </button>

                    {/* Low Risk */}
                    <button
                        type="button"
                        className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${riskFilters.length === 1 && riskFilters[0] === 'low' ? 'border-gray-900' : 'border-gray-200/80'}`}
                        onClick={() => setRiskFilters(['low'])}
                    >
                        <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${riskFilters.length === 1 && riskFilters[0] === 'low' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                        <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                            <CheckCircle className="w-4 h-4" style={{ color: '#69BE28' }} />
                        </div>
                        <p className="text-2xl font-bold text-gray-900 leading-none">{riskDistribution.low}</p>
                        <p className="text-xs text-gray-500 mt-1.5">Low Risk</p>
                        <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                            Sites where drivers have a low average risk score.
                        </span>
                    </button>

                    {/* Medium Risk */}
                    <button
                        type="button"
                        className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${riskFilters.length === 1 && riskFilters[0] === 'medium' ? 'border-gray-900' : 'border-gray-200/80'}`}
                        onClick={() => setRiskFilters(['medium'])}
                    >
                        <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${riskFilters.length === 1 && riskFilters[0] === 'medium' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                        <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(245,158,11,0.12)' }}>
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900 leading-none">{riskDistribution.medium}</p>
                        <p className="text-xs text-gray-500 mt-1.5">Medium Risk</p>
                        <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                            Sites where drivers have a medium average risk score.
                        </span>
                    </button>

                    {/* High Risk */}
                    <button
                        type="button"
                        className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${riskFilters.length === 1 && riskFilters[0] === 'high' ? 'border-gray-900' : 'border-gray-200/80'}`}
                        onClick={() => setRiskFilters(['high'])}
                    >
                        <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${riskFilters.length === 1 && riskFilters[0] === 'high' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                        <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(239,68,68,0.12)' }}>
                            <ShieldAlert className="w-4 h-4 text-red-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900 leading-none">{riskDistribution.high}</p>
                        <p className="text-xs text-gray-500 mt-1.5">High Risk</p>
                        <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                            Sites where drivers have a high average risk score.
                        </span>
                    </button>
                </div>

                {/* Filters bar */}
                <div className={`${CARD} p-4 mb-5`}>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search sites..."
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm"
                                style={{ '--tw-ring-color': 'rgba(105,190,40,0.5)' } as React.CSSProperties}
                            />
                        </div>
                        <div className="sm:w-48">
                            <MultiSelectFilter
                                label="Risk levels"
                                allLabel="All Risk Levels"
                                options={[
                                    { value: 'low', label: 'Low Risk' },
                                    { value: 'medium', label: 'Medium Risk' },
                                    { value: 'high', label: 'High Risk' },
                                ]}
                                values={riskFilters}
                                onApply={(values) => { setRiskFilters(values); setCurrentPage(1); }}
                            />
                        </div>
                        <div className="sm:w-48">
                            <MultiSelectFilter
                                label="Site status"
                                allLabel="All Sites"
                                options={[
                                    { value: 'active', label: 'Active Sites' },
                                    { value: 'inactive', label: 'Inactive Sites' },
                                ]}
                                values={statusFilters}
                                onApply={(values) => { setStatusFilters(values); setCurrentPage(1); }}
                            />
                        </div>
                        {breakdownCategories.length > 0 && (
                            <div className="sm:w-56">
                                <MultiSelectFilter
                                    label="Compliance issues"
                                    allLabel="All Issues"
                                    options={breakdownCategories.map(cat => ({
                                        value: cat.key,
                                        label: cat.label.charAt(0).toUpperCase() + cat.label.slice(1),
                                    }))}
                                    values={issueFilters}
                                    onApply={(values) => { setIssueFilters(values); setCurrentPage(1); }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Active filter chips + count */}
                {(() => {
                    const chips: { key: string; label: string; onRemove: () => void }[] = [];

                    if (searchQuery) chips.push({
                        key: 'search',
                        label: `Search: "${searchQuery}"`,
                        onRemove: () => { setSearchQuery(''); setCurrentPage(1); },
                    });

                    riskFilters.forEach(r => chips.push({
                        key: `risk-${r}`,
                        label: `Risk: ${r.charAt(0).toUpperCase() + r.slice(1)}`,
                        onRemove: () => { setRiskFilters(current => current.filter(v => v !== r)); setCurrentPage(1); },
                    }));

                    statusFilters.forEach(s => chips.push({
                        key: `status-${s}`,
                        label: s === 'active' ? 'Active Sites' : 'Inactive Sites',
                        onRemove: () => { setStatusFilters(current => current.filter(v => v !== s)); setCurrentPage(1); },
                    }));

                    issueFilters.forEach(f => {
                        const cat = breakdownCategories.find(c => c.key === f);
                        chips.push({
                            key: `issue-${f}`,
                            label: cat ? cat.label.charAt(0).toUpperCase() + cat.label.slice(1) : f,
                            onRemove: () => { setIssueFilters(current => current.filter(v => v !== f)); setCurrentPage(1); },
                        });
                    });

                    const clearAll = () => {
                        setSearchQuery('');
                        setRiskFilters([]);
                        setStatusFilters([]);
                        setIssueFilters([]);
                        setCurrentPage(1);
                    };

                    return (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                            {chips.length > 0 && (
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mr-1">Filters</span>
                            )}
                            {chips.map(chip => (
                                <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
                                    {chip.label}
                                    <button
                                        onClick={chip.onRemove}
                                        className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </span>
                            ))}
                            {chips.length > 1 && (
                                <button onClick={clearAll} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
                                    Clear all
                                </button>
                            )}
                        </div>
                    );
                })()}

                {/* Table */}
                <div className={`${CARD} overflow-hidden mb-5`}>
                    {/* Card header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#69BE28]/10">
                                <Building2 className="h-4 w-4 text-[#4a8a1c]" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Site register</h3>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    {filteredData.length < leaderboard.length
                                        ? `${filteredData.length} matching of ${leaderboard.length} sites`
                                        : `${leaderboard.length} ${leaderboard.length === 1 ? 'site' : 'sites'}`}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Column headers */}
                    <div className="flex items-center gap-4 px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                        <button
                            onClick={() => toggleSort('rank')}
                            className="w-16 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                            Rank
                            {sortBy === 'rank' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                        </button>
                        <button
                            onClick={() => toggleSort('score')}
                            className="w-16 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                            Score
                            {sortBy === 'score' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                        </button>
                        <button
                            onClick={() => toggleSort('name')}
                            className="w-40 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                            Site
                            {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                        </button>
                        <button
                            onClick={() => toggleSort('drivers')}
                            className="w-20 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                            Drivers
                            {sortBy === 'drivers' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                        </button>
                        <div className="flex-1 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</div>
                        <div className="w-20 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Actions</div>
                    </div>

                    {/* Body */}
                    {paginatedData.length === 0 ? (
                        <div className="px-6 py-12 text-center text-gray-500">
                            <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p>No sites match your search criteria</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {paginatedData.map((site) => {
                                const issues: { count: number; label: string; color: string; filter: string; icon: React.ReactNode }[] = [];

                                for (const category of breakdownCategories) {
                                    const count = site.breakdown[category.key] || 0;
                                    if (count > 0) {
                                        const IconComponent = iconComponents[category.icon] || AlertTriangle;
                                        issues.push({
                                            count,
                                            label: category.label,
                                            color: severityColors[category.severity] || 'gray',
                                            filter: category.filter,
                                            icon: <IconComponent className="w-3 h-3 mr-1" />,
                                        });
                                    }
                                }

                                const totalIssues = issues.reduce((sum, i) => sum + i.count, 0);
                                const hasNoIssues = totalIssues === 0;
                                const isExpanded = expandedSites.has(site.siteId);
                                const maxVisible = 5;
                                const displayIssues = isExpanded ? issues : issues.slice(0, maxVisible);
                                const remainingCount = issues.length - maxVisible;

                                return (
                                    <div
                                        key={site.siteId}
                                        className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50/60 transition-colors"
                                    >
                                        {/* Rank */}
                                        <div className="w-16 flex items-center justify-center">
                                            {getRankBadge(site.rank)}
                                        </div>

                                        {/* Score */}
                                        <div className="w-16 flex items-center">
                                            <span
                                                className="px-2 py-0.5 rounded text-xs font-bold text-white"
                                                style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                            >
                                                {site.totalScore.toFixed(0)}
                                            </span>
                                        </div>

                                        {/* Site Name */}
                                        <div className="w-40 min-w-0 relative group">
                                            <p className="text-sm font-medium text-gray-900 truncate">{site.siteName}</p>
                                            <div className="absolute left-0 bottom-full mb-1.5 px-2 py-1 bg-gray-800 text-white text-xs rounded-lg hidden group-hover:block z-50 shadow-lg whitespace-nowrap pointer-events-none">
                                                {site.siteName}
                                            </div>
                                        </div>

                                        {/* Drivers */}
                                        <button
                                            className="w-20 flex items-center text-sm text-gray-600 hover:text-green-700 transition-colors group"
                                            onClick={() => navigate(`/drivers?site=${site.siteId}`)}
                                            title="View drivers for this site"
                                        >
                                            <Users className="w-4 h-4 text-gray-400 mr-1.5 group-hover:text-green-600 transition-colors" />
                                            {site.totalDrivers}
                                        </button>

                                        {/* Status badges */}
                                        <div className="flex-1 flex items-center gap-3 flex-wrap min-w-0">
                                            {hasNoIssues ? (
                                                <span
                                                    className="inline-flex min-h-7 items-center px-1 text-base leading-none text-gray-300"
                                                    title="No open issues"
                                                    aria-label="No open issues"
                                                >
                                                    -
                                                </span>
                                            ) : (
                                                <>
                                                    {displayIssues.map((issue, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/drivers?site=${site.siteId}&show=${issue.filter}`); }}
                                                            className={`relative inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-1 text-xs font-semibold underline-offset-4 transition-colors before:absolute before:-inset-1 hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                                                issue.color === 'red'    ? 'text-red-600 focus:ring-red-600' :
                                                                issue.color === 'yellow' ? 'text-amber-700 focus:ring-amber-700' :
                                                                'text-gray-600 focus:ring-gray-400'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`h-4 w-[3px] rounded-full ${
                                                                    issue.color === 'red'    ? 'bg-red-500' :
                                                                    issue.color === 'yellow' ? 'bg-amber-400' :
                                                                    'bg-gray-300'
                                                                }`}
                                                                aria-hidden="true"
                                                            />
                                                            <span className="tabular-nums">{issue.count}</span>
                                                            {issue.label}
                                                        </button>
                                                    ))}
                                                    {remainingCount > 0 && !isExpanded && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpandedSites(prev => new Set(prev).add(site.siteId)); }}
                                                            className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                                                        >
                                                            +{remainingCount} more
                                                        </button>
                                                    )}
                                                    {isExpanded && issues.length > maxVisible && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault(); e.stopPropagation();
                                                                setExpandedSites(prev => { const next = new Set(prev); next.delete(site.siteId); return next; });
                                                            }}
                                                            className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                                                        >
                                                            show less
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center justify-end gap-1.5 flex-shrink-0">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setSelectedViewSite(site); setShowViewSite(true); }}
                                                className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                title="View site"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleOpenEdit(site.siteId); }}
                                                className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                title="Edit site"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setSiteToDelete({ id: site.siteId, name: site.siteName, totalDrivers: site.totalDrivers }); }}
                                                className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-600"
                                                title="Delete site"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-4 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5)           pageNum = i + 1;
                                    else if (currentPage <= 3)     pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else                           pageNum = currentPage - 2 + i;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'text-white'
                                                    : 'border border-gray-300 hover:bg-gray-100'
                                            }`}
                                            style={currentPage === pageNum ? { backgroundColor: '#69BE28' } : {}}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Site Trends */}
                <div className={`${CARD} p-6 mb-5`}>
                    <div className="flex items-center gap-2.5 mb-5">
                        <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                            <BarChart3 className="w-4 h-4" style={{ color: '#69BE28' }} />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Site Trends</h3>
                    </div>
                    <SiteTrendsTab refreshKey={trendsRefreshKey} />
                </div>

                {/* Scoring legend */}
                <div className={`${CARD} p-4`}>
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
                        <span className="text-gray-500 font-medium">Score = Average driver risk (lower is better)</span>
                        <div className="h-4 w-px bg-gray-300" />
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1.5">
                                <span className="h-4 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: '#69BE28' }} aria-hidden="true" />
                                <span className="text-gray-600">Low &lt;{lowMediumBoundary}</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="h-4 w-[3px] rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                                <span className="text-gray-600">Medium {lowMediumBoundary}–{mediumHighBoundary - 1}</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="h-4 w-[3px] rounded-full bg-red-500 flex-shrink-0" aria-hidden="true" />
                                <span className="text-gray-600">High {mediumHighBoundary}+</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <AddSiteModal
                isOpen={showAddSite}
                onClose={() => setShowAddSite(false)}
                onSubmit={handleCreateSite}
                isCreating={isCreating}
            />
            <EditSiteModal
                isOpen={showEditSite}
                onClose={() => { setShowEditSite(false); setSelectedSite(null); }}
                onSubmit={handleUpdateSite}
                isUpdating={isUpdating}
                site={selectedSite}
            />
            <DeleteSiteModal
                site={siteToDelete}
                onClose={() => setSiteToDelete(null)}
                onConfirm={handleDeleteSite}
                isDeleting={isDeleting}
            />
            <SiteViewModal
                site={selectedViewSite}
                isOpen={showViewSite}
                onClose={() => { setShowViewSite(false); setSelectedViewSite(null); }}
                onEdit={selectedViewSite ? () => { setShowViewSite(false); handleOpenEdit(selectedViewSite.siteId); } : undefined}
                breakdownCategories={breakdownCategories}
            />
        </div>
    );
};

export default SitesPage;
